import { REVENUE_SERIES_DAY_MAX_DAYS, businessDayStartUtc } from '@nadoutong/contracts';
import type { FinancePnlWindow, RevenueSeriesGranularity } from '@nadoutong/contracts';
import type { BrandFilter } from '../../api/revenue.ts';

/**
 * URL-state parsers for the metrics hub (`/business/metrics`, epic
 * #1643). Everything on the page is deep-linkable: `?tab=`, `?window=`
 * (+ `from`/`to` when custom), `?brand=`, `?tenantSort=`.
 */

export const METRICS_TABS = [
  'overview',
  'models',
  'platform_sku',
  'upstream',
  'tenants',
  'usage',
  'pnl',
  'sku',
] as const;
export type MetricsTab = (typeof METRICS_TABS)[number];

/** Default tab carries NO `tab` param (mirrors Customer360's selectTab). */
export const DEFAULT_METRICS_TAB: MetricsTab = 'overview';

export function parseMetricsTab(raw: string | null): MetricsTab {
  const match = METRICS_TABS.find((tab) => tab === raw);
  return match ?? DEFAULT_METRICS_TAB;
}

/** The shared filter-row window set (= RevenueWindow / OpsUsageWindow). */
export type MetricsWindow = 'today' | '7d' | '30d' | 'custom';

export const METRICS_WINDOWS: readonly MetricsWindow[] = ['today', '7d', '30d', 'custom'];

/**
 * Read `?window=` back into a window every non-pnl tab can serve.
 *
 * The pnl tab (S5) additionally accepts `this_month`/`last_month`/`all`
 * (`FinancePnlWindow`), so after switching tabs the URL may hold a
 * pnl-only value. Non-pnl endpoints reject those, so they narrow to the
 * nearest fixed window: month-scale and all-time values → `30d` (the
 * widest fixed window this surface offers). Unknown/absent → `7d`.
 */
export function narrowWindow(raw: string | null): MetricsWindow {
  const match = METRICS_WINDOWS.find((window) => window === raw);
  if (match !== undefined) return match;
  if (raw === 'this_month' || raw === 'last_month' || raw === 'all') return '30d';
  return '7d';
}

/**
 * The pnl tab's extended pill set (S5): the shared four plus the
 * month-scale/all-time presets `useFinancePnl` accepts. Ordered so the
 * shared pills keep their position and the extras appear appended when
 * the pnl tab activates.
 */
export const PNL_WINDOWS: readonly FinancePnlWindow[] = [
  'today',
  '7d',
  '30d',
  'this_month',
  'last_month',
  'all',
  'custom',
];

/**
 * Read `?window=` for the pnl tab, which serves the full
 * `FinancePnlWindow` set. Unknown/absent → `7d`, matching the hub's
 * shared default so the active pill never jumps on a tab switch when no
 * window param is present.
 */
export function parsePnlWindow(raw: string | null): FinancePnlWindow {
  const match = PNL_WINDOWS.find((window) => window === raw);
  return match ?? '7d';
}

export function parseBrand(raw: string | null): BrandFilter {
  return raw === 'nadoutong' || raw === 'nxtpath' ? raw : '';
}

export type TenantSort = 'spend' | 'profit';

/** Mockup default: the consumption leaderboard (`spend`). */
export function parseTenantSort(raw: string | null): TenantSort {
  return raw === 'profit' ? 'profit' : 'spend';
}

export interface MetricsRange {
  readonly from?: string;
  readonly to?: string;
}

/**
 * Shared filter state the hub page resolves once and hands each tab.
 * `customFrom`/`customTo` are ALWAYS concrete ISO bounds — the page
 * falls back to its mount-time defaults when the URL omits them, so no
 * tab ever issues an unbounded `window=custom` request (server 400s).
 */
export interface MetricsTabContext {
  readonly window: MetricsWindow;
  readonly brand: BrandFilter;
  readonly customFrom: string;
  readonly customTo: string;
  readonly range: MetricsRange;
}

/**
 * Order a custom `[from, to)` pair: when both bounds parse and
 * `from > to`, the two strings are swapped (values preserved verbatim —
 * no reformatting). This is THE single normalisation point for
 * operator-entered custom bounds: a reversed pair would otherwise fan
 * out into guaranteed-400 requests on every free-form endpoint, and a
 * negative span would fool the granularity rule into `hour`. Bounds
 * that fail to parse pass through untouched — the server's validation
 * owns that error surface.
 */
export function normalizeCustomBounds(from: string, to: string): { readonly from: string; readonly to: string } {
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (Number.isFinite(fromMs) && Number.isFinite(toMs) && fromMs > toMs) {
    return { from: to, to: from };
  }
  return { from, to };
}

/**
 * Concrete `[from, to)` bounds for the endpoints that take a free-form
 * pair instead of a named window (`/by-model`, `/by-tenant`) — without
 * this they'd silently fall back to the server's 366-day default span
 * while the filter row claims "7 天".
 *
 * Mirrors the server's `resolveWindow` (#1001): `today` starts at the
 * current UTC+8 business-day midnight, `7d`/`30d` at `now - N days`;
 * `to` is omitted (the server defaults it to now). `custom` passes the
 * page-resolved bounds through untouched (never undefined — see
 * `MetricsTabContext`).
 *
 * `now` is captured once per call and the page memoizes the result per
 * window change — a deliberate trade-off: the bounds stay frozen for
 * the duration of a page visit so React Query keys are stable (no
 * refetch churn from a sliding `now` on every render), at the cost of
 * the window not continuously sliding forward until the operator
 * changes a filter or reloads.
 */
export function windowRange(
  window: MetricsWindow,
  customFrom: string,
  customTo: string,
  now: Date = new Date(),
): MetricsRange {
  if (window === 'custom') {
    // Reversed bounds are normalised here, at the single spot every tab
    // reads its range from — downstream consumers (queries and
    // `planGroupSeries`) can rely on `from <= to`.
    return normalizeCustomBounds(customFrom, customTo);
  }
  if (window === 'today') {
    return { from: businessDayStartUtc(now).toISOString() };
  }
  const days = window === '7d' ? 7 : 30;
  return { from: new Date(now.getTime() - days * 24 * 60 * 60 * 1000).toISOString() };
}

// ─── Group-series request planning (#1675) ───────────────────────────

const HOUR_MS = 60 * 60 * 1000;
const GROUP_SERIES_DAY_MS = 24 * HOUR_MS;

/**
 * Resolved request shape for the `/by-sku/series` + `/by-upstream/series`
 * endpoints, derived from the hub's shared window. `from`/`to` mirror
 * `MetricsRange` semantics (undefined `to` → server-side "now").
 * `clamped` marks a custom range that exceeded the server's 90-day
 * day-granularity cap and was narrowed to the trailing 90 days — the
 * tab surfaces an explicit note, and uses the SAME clamped bounds for
 * its aggregate table so one tab never mixes two windows.
 */
export interface GroupSeriesPlan {
  readonly granularity: RevenueSeriesGranularity;
  readonly from?: string;
  readonly to?: string;
  readonly clamped: boolean;
}

/**
 * Granularity rule (operator ask, #1675): a window of at most 24 hours
 * reads better hour-by-hour — `today` always qualifies (a UTC+8
 * business day never exceeds 24h), and a custom range qualifies when
 * `to - from <= 24h`. Everything else buckets by day. Hour requests
 * stay well inside the server's 48h cap by construction.
 *
 * Custom ranges longer than `REVENUE_SERIES_DAY_MAX_DAYS` (90) days
 * would 400 server-side (the endpoint rejects rather than clamps), so
 * the plan narrows `from` to the trailing 90 days and flags `clamped`.
 * Unparseable custom bounds pass through unchanged — the server's
 * validation owns that error surface.
 */
export function planGroupSeries(window: MetricsWindow, range: MetricsRange): GroupSeriesPlan {
  if (window === 'today') {
    return { granularity: 'hour', from: range.from, to: range.to, clamped: false };
  }
  if (window !== 'custom') {
    return { granularity: 'day', from: range.from, to: range.to, clamped: false };
  }
  // The page hands over `windowRange` output (already normalised), but
  // re-apply `normalizeCustomBounds` so a direct caller with reversed
  // bounds can't compute a negative span (which would masquerade as an
  // `hour`-qualifying window) or emit a guaranteed-400 request.
  const ordered =
    range.from !== undefined && range.to !== undefined
      ? normalizeCustomBounds(range.from, range.to)
      : { from: range.from, to: range.to };
  const fromMs = ordered.from === undefined ? Number.NaN : Date.parse(ordered.from);
  const toMs = ordered.to === undefined ? Number.NaN : Date.parse(ordered.to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) {
    return { granularity: 'day', from: ordered.from, to: ordered.to, clamped: false };
  }
  const spanMs = toMs - fromMs;
  if (spanMs <= GROUP_SERIES_DAY_MS) {
    return { granularity: 'hour', from: ordered.from, to: ordered.to, clamped: false };
  }
  if (spanMs > REVENUE_SERIES_DAY_MAX_DAYS * GROUP_SERIES_DAY_MS) {
    return {
      granularity: 'day',
      from: new Date(toMs - REVENUE_SERIES_DAY_MAX_DAYS * GROUP_SERIES_DAY_MS).toISOString(),
      to: ordered.to,
      clamped: true,
    };
  }
  return { granularity: 'day', from: ordered.from, to: ordered.to, clamped: false };
}
